// src/Components/Header/Header.js
import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AllPostContext } from "../../contextStore/AllPostContext";
import { PostContext } from "../../contextStore/PostContext";
import { AuthContext } from "../../contextStore/AuthContext";
import { LocationContext } from "../../contextStore/LocationContext";
import client from "../../api/client";
import "./Header.css";

// Icons
import SellButtonPlus from "../../assets/SellButtonPlus";
import MenuIcon from "../../assets/MenuIcon";
import ProfileIcon from "../../assets/ProfileIcon";
import ContactIcon from "../../assets/ContactIcon";

function Header() {
  const { allPost } = useContext(AllPostContext);
  const { setPostContent } = useContext(PostContext);
  const { user, setUser } = useContext(AuthContext);
  const { locationLabel } = useContext(LocationContext);

  const navigate = useNavigate();

  // Auth UI
  const [loggedInUser, setLoggedInUser] = useState(null);

  // Dropdowns / menus
  const [showCategories, setShowCategories] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const categoriesRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Dynamic categories loaded from API
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Sync loggedInUser from context or localStorage
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (user) {
      setLoggedInUser(user);
    } else if (stored) {
      try {
        setLoggedInUser(JSON.parse(stored));
      } catch (e) {
        setLoggedInUser(null);
      }
    } else {
      setLoggedInUser(null);
    }
  }, [user]);

  // Fetch categories from backend (same pattern as Create/Edit)
  useEffect(() => {
    let mounted = true;
    async function fetchCategories() {
      setLoadingCategories(true);
      try {
        const res = await client.get("/api/categories");
        if (!mounted) return;
        if (res.data && Array.isArray(res.data.categories)) {
          // Normalize to objects { name, slug }
          const normalized = res.data.categories.map((c) => {
            if (typeof c === "string") {
              const name = c;
              const slug = name.toLowerCase().replace(/\s+/g, "-");
              return { name, slug };
            }
            return {
              name: c.name || c.display_name || String(c).trim(),
              slug: c.slug || (c.name ? c.name.toLowerCase().replace(/\s+/g, "-") : null),
            };
          });
          setCategories(normalized);
        } else {
          // fallback: empty
          setCategories([]);
        }
      } catch (err) {
        console.error("Header: failed to load categories", err);
        setCategories([]);
      } finally {
        if (mounted) setLoadingCategories(false);
      }
    }
    fetchCategories();
    return () => { mounted = false; };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (categoriesRef.current && !categoriesRef.current.contains(event.target)) {
        setShowCategories(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCategorySelect = (category) => {
    // category may be object {name, slug} or string
    let slug = "";
    if (!category) return;
    if (typeof category === "string") slug = category.toLowerCase().replace(/\s+/g, "-");
    else slug = (category.slug || (category.name || "").toLowerCase().replace(/\s+/g, "-"));

    navigate(`/category/${slug}`);
    setShowCategories(false);
  };

  // Logout
  const logoutHandler = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser && setUser(null);
    setLoggedInUser(null);
    setShowUserMenu(false);
    navigate("/login");
  };

  const handleProfileClick = () => {
    navigate("/profile");
    setShowUserMenu(false);
  };

  const handleContactClick = (event) => {
    // prevent any parent click handlers, just in case
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    // open dialer
    window.location.href = "tel:+919825527848";

    // close mobile menu if it was open
    setShowMobileMenu(false);
  };


  return (
    <div className="headerParentDiv">
      <div className="headerChildDiv">
        {/* Mobile Menu Button */}
        <div
          className="mobileMenuBtn"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
          aria-label="Toggle mobile menu"
        >
          <MenuIcon />
        </div>

        {/* Brand / Logo - Amazon-style logo only */}
        <div className="brandSection" onClick={() => navigate("/")}>
          <img src="/logo1.png" alt="OLD MACHINE MARKET" className="brandLogo" />
        </div>

        {/* Categories Dropdown */}
        <div className="categoriesDropdown" ref={categoriesRef}>
          <button className="categoriesBtn" onClick={() => setShowCategories(!showCategories)}>
            <span>Categories</span>
            <svg className={`arrow ${showCategories ? "rotate" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showCategories && (
            <div className="categoriesMenu animated-dropdown">
              <div className="categoriesHeader"><h3>All Categories</h3></div>

              {loadingCategories ? (
                <div className="categoryLoading">Loading...</div>
              ) : categories.length ? (
                categories.map((cat, index) => (
                  <div
                    key={cat.slug || `${cat.name}-${index}`}
                    className="categoryItem"
                    onClick={() => handleCategorySelect(cat)}
                  >
                    <span className="categoryName">{cat.name}</span>
                  </div>
                ))
              ) : (
                // Fallback static list if API returned nothing
                <div className="categoryFallback">
                  <div className="categoryItem" onClick={() => handleCategorySelect("Textile Industries")}>Textile Industries</div>
                  <div className="categoryItem" onClick={() => handleCategorySelect("Food Industries")}>Food Industries</div>
                  <div className="categoryItem" onClick={() => handleCategorySelect("Construction")}>Construction</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Spacer to push user actions to the right */}
        <div className="headerSpacer"></div>

        {/* User Actions */}
        <div className="userActions">
          {/* Contact Button */}
          <button
            className="contactButton animated-button"
            onClick={handleContactClick}
            title="Contact Us"
          >
            <ContactIcon />
            <span className="contactText">Contact</span>
          </button>

          {loggedInUser ? (
            <div className="userSection" ref={userMenuRef} style={{ position: "relative" }}>
              <div
                className="userProfile animated-button"
                onClick={() => setShowUserMenu((s) => !s)}
                role="button"
                aria-haspopup="true"
                aria-expanded={showUserMenu}
              >
                <button
                  className="profileAvatar"
                  onClick={(e) => { e.stopPropagation(); navigate("/profile"); setShowUserMenu(false); }}
                  aria-label="Go to profile"
                  title="Profile"
                >
                  {loggedInUser.name ? loggedInUser.name.charAt(0).toUpperCase() : "U"}
                </button>

                <span className="userName">{loggedInUser.name || "User"}</span>

                <svg className={`arrow ${showUserMenu ? "rotate" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {showUserMenu && (
                <div className="userDropdown animated-dropdown" style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 1200 }}>
                  <div className="userDropdownHeader">
                    <div className="profileAvatar large" onClick={handleProfileClick} style={{ cursor: "pointer" }}>
                      {loggedInUser.name ? loggedInUser.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div className="userInfo">
                      <span className="userDisplayName">{loggedInUser.name || "User"}</span>
                      <span className="userEmail">{loggedInUser.email || ""}</span>
                    </div>
                  </div>

                  <div className="dropdownDivider"></div>

                  <div className="dropdownItem" onClick={handleProfileClick}>
                    <ProfileIcon />
                    <span>Profile</span>
                  </div>

                  <div className="dropdownItem logoutItem" onClick={logoutHandler}>
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="authButtons">
              <Link to="/login" className="loginBtn animated-button">Login</Link>
              <Link to="/signup" className="signupBtn animated-button">Sign Up</Link>
            </div>
          )}

          {/* Sell Button */}
          <Link to={loggedInUser ? "/create" : "/login"} className="sellLink">
            <div className="sellButton animated-button pulse-animation">
              <SellButtonPlus />
              <span className="sellText">SELL</span>
            </div>
          </Link>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div className="mobileMenu animated-slide-in" ref={mobileMenuRef}>
            <div className="mobileMenuHeader">
              <div className="mobileBrand">
                <img src="/logo1.png" alt="OLD MACHINE MARKET" className="mobileLogo" />
                <span>OLD MACHINE MARKET</span>
              </div>
              <button onClick={() => setShowMobileMenu(false)} className="closeBtn">×</button>
            </div>

            <div className="mobileMenuContent">
              {/* Mobile Categories */}
              <div className="mobileCategories">
                <h3>Categories</h3>
                {loadingCategories ? (
                  <div>Loading...</div>
                ) : categories.length ? (
                  categories.map((cat, idx) => (
                    <div key={cat.slug || `${cat.name}-${idx}`} className="mobileCategoryItem" onClick={() => { handleCategorySelect(cat); setShowMobileMenu(false); }}>
                      {cat.name}
                    </div>
                  ))
                ) : (
                  <>
                    <div className="mobileCategoryItem" onClick={() => { handleCategorySelect("Textile Industries"); setShowMobileMenu(false); }}>Textile Industries</div>
                    <div className="mobileCategoryItem" onClick={() => { handleCategorySelect("Food Industries"); setShowMobileMenu(false); }}>Food Industries</div>
                    <div className="mobileCategoryItem" onClick={() => { handleCategorySelect("Construction"); setShowMobileMenu(false); }}>Construction</div>
                  </>
                )}
              </div>

              {/* Mobile User Section */}
              {loggedInUser ? (
                <div className="mobileUserSection">
                  <div className="mobileUserInfo">
                    <div className="profileAvatar large">
                      {loggedInUser.name ? loggedInUser.name.charAt(0).toUpperCase() : "U"}
                    </div>
                    <div className="mobileUserDetails">
                      <span className="mobileUserName">{loggedInUser.name || "User"}</span>
                      <span className="mobileUserEmail">{loggedInUser.email || ""}</span>
                    </div>
                  </div>

                  <div className="mobileMenuItems">
                    <div className="mobileMenuItem" onClick={handleProfileClick}>
                      <ProfileIcon />
                      <span>Profile</span>
                    </div>
                    <div
                      className="mobileMenuItem"
                      onClick={handleContactClick}
                    >
                      <ContactIcon />
                      <span>Contact Us</span>
                    </div>

                    <div className="mobileMenuItem mobileLogout" onClick={logoutHandler}>
                      <span>Logout</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mobileAuth">
                  <div className="mobileAuthButtons">
                    <Link to="/login" className="mobileLoginBtn" onClick={() => setShowMobileMenu(false)}>
                      Login
                    </Link>
                    <Link to="/signup" className="mobileSignupBtn" onClick={() => setShowMobileMenu(false)}>
                      Sign Up
                    </Link>
                  </div>
                </div>
              )}

              {/* Mobile Sell Button */}
              <div className="mobileSellSection">
                <Link
                  to={loggedInUser ? "/create" : "/login"}
                  className="mobileSellButton pulse-animation"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <SellButtonPlus />
                  <span>Sell Your Machine</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Header;
